import { Platform as CdkPlatform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { shareOneReplay } from '@examdojo/rxjs';
import { Platform as IonicPlatform } from '@ionic/angular/standalone';
import { distinctUntilChanged, filter, fromEvent, map, startWith } from 'rxjs';
import { LAYOUT_BREAKPOINTS, LayoutBreakpoints } from './layout-breakpoints';
import { Browser, MouseButton, OperatingSystem } from './platform.model';

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  private readonly cdkPlatform = inject(CdkPlatform);
  private readonly ionicPlatform = inject(IonicPlatform);

  private readonly layoutBreakpoints = inject<LayoutBreakpoints>(LAYOUT_BREAKPOINTS);
  private readonly document = inject<Document>(DOCUMENT);

  readonly isBrowser = this.cdkPlatform.isBrowser;
  readonly operatingSystem = this.guessOperatingSystem();
  readonly browser = this.guessBrowser();

  readonly isMobile = this.ionicPlatform.is('mobile');
  readonly isTablet = this.ionicPlatform.is('tablet');
  readonly isDesktop = this.ionicPlatform.is('desktop');

  readonly isIos = this.ionicPlatform.is('ios');
  readonly isNative = Capacitor.isNativePlatform();
  readonly isSafari = this.browser === Browser.Safari;

  readonly smallBreakpoint = this.layoutBreakpoints.small;
  readonly mediumBreakpoint = this.layoutBreakpoints.medium;
  readonly largeBreakpoint = this.layoutBreakpoints.large;

  readonly isTouchDevice = 'ontouchstart' in this.document.documentElement;

  readonly resize$ = this.ionicPlatform.resize.pipe(
    map(() => ({
      width: this.ionicPlatform.width(),
      height: this.ionicPlatform.height(),
    })),
  );

  readonly width$ = this.resize$.pipe(
    startWith({ width: this.width }),
    map(({ width }) => width),
    shareOneReplay(),
  );

  readonly height$ = this.resize$.pipe(
    startWith({ height: this.height }),
    map(({ height }) => height),
    shareOneReplay(),
  );

  readonly isSmall$ = this.resize$.pipe(
    startWith({ width: this.width }),
    map(({ width }) => width < this.smallBreakpoint),
    distinctUntilChanged(),
    shareOneReplay(),
  );

  readonly isMedium$ = this.resize$.pipe(
    startWith({ width: this.width }),
    map(({ width }) => width < this.mediumBreakpoint),
    distinctUntilChanged(),
    shareOneReplay(),
  );

  readonly isLarge$ = this.resize$.pipe(
    startWith({ width: this.width }),
    map(({ width }) => width >= this.largeBreakpoint),
    distinctUntilChanged(),
    shareOneReplay(),
  );

  readonly isSmall = toSignal(this.isSmall$, { requireSync: true });
  readonly isMedium = toSignal(this.isMedium$, { requireSync: true });
  readonly isLarge = toSignal(this.isLarge$, { requireSync: true });

  protected readonly visibilitychange$ = fromEvent(document, 'visibilitychange');

  protected readonly pageVisible$ = this.visibilitychange$.pipe(filter(() => document.visibilityState === 'visible'));

  get width() {
    return this.ionicPlatform.width();
  }

  get height() {
    return this.ionicPlatform.height();
  }

  isPrimaryMouseButtonEvent(e: MouseEvent): boolean {
    if (e.button !== MouseButton.Primary) {
      return false;
    }

    // On MacBook touch pads the right click can be simulated by pressing ctrl key while clicking the primary button (touchpad).
    if (this.operatingSystem === OperatingSystem.MacOS && e.ctrlKey) {
      return false;
    }

    return true;
  }

  isSecondaryMouseButtonEvent(e: MouseEvent): boolean {
    if (e.button === MouseButton.Secondary) {
      return true;
    }

    if (this.operatingSystem === OperatingSystem.MacOS && e.ctrlKey && e.button === MouseButton.Primary) {
      return true;
    }

    return false;
  }

  eventHasCmdOrCtrlModifier(event: KeyboardEvent | MouseEvent): boolean {
    return (
      (event.metaKey && this.operatingSystem === OperatingSystem.MacOS) ||
      (event.ctrlKey && this.operatingSystem !== OperatingSystem.MacOS)
    );
  }

  private guessOperatingSystem(): OperatingSystem {
    if (this.cdkPlatform.ANDROID) {
      return OperatingSystem.Android;
    }

    if (this.cdkPlatform.IOS) {
      return OperatingSystem.IOS;
    }

    if (/windows/i.test(navigator.userAgent)) {
      return OperatingSystem.Windows;
    }

    if (/Macintosh/i.test(navigator.userAgent)) {
      return OperatingSystem.MacOS;
    }

    if (/Linux/i.test(navigator.userAgent)) {
      return OperatingSystem.Linux;
    }

    return OperatingSystem.Other;
  }

  private guessBrowser(): Browser {
    if (this.cdkPlatform.EDGE) {
      return Browser.Edge;
    }

    if (this.cdkPlatform.FIREFOX) {
      return Browser.Firefox;
    }

    if (this.cdkPlatform.SAFARI) {
      return Browser.Safari;
    }

    if (this.cdkPlatform.WEBKIT) {
      return Browser.WebKit;
    }

    if (this.cdkPlatform.BLINK) {
      return Browser.Blink;
    }

    return Browser.Other;
  }
}
